import { useState } from 'react';

import { getMockUserProfile, type MockPaymentMethod } from '@/lib/mock-data';
import { Badge, Button, Card, CardTitle, toast } from '@/components/ui';

/**
 * AccountPaymentMethods — `/account/payment-methods`. Listado de tarjetas
 * guardadas. Las acciones "Predeterminar" y "Quitar" operan sobre estado
 * local; la persistencia real depende de la tokenización de tarjetas del
 * gateway de pago, que se integra en un sprint posterior.
 */
export function AccountPaymentMethods(): JSX.Element {
  const [methods, setMethods] = useState<ReadonlyArray<MockPaymentMethod>>(
    () => getMockUserProfile().paymentMethods,
  );

  function setDefault(id: string): void {
    setMethods((cur) => cur.map((m) => ({ ...m, isDefault: m.id === id })));
    toast.success({ title: 'Método predeterminado actualizado' });
  }

  function remove(id: string): void {
    setMethods((cur) => cur.filter((m) => m.id !== id));
    toast.info({ title: 'Método eliminado' });
  }

  return (
    <div className="flex flex-col gap-6">
      <Card variant="default" padded>
        <div className="flex items-start justify-between gap-3">
          <CardTitle>Métodos de pago</CardTitle>
          <Button variant="secondary" size="sm" disabled>
            Agregar tarjeta
          </Button>
        </div>
        <p className="mt-2 text-sm text-fc-muted">
          Tarjetas guardadas para checkout 1-clic. Las gestionamos tokenizadas — nunca guardamos
          números completos.
        </p>
      </Card>

      {methods.length === 0 ? (
        <Card variant="ghost">
          <p className="text-fc-muted">No tenés tarjetas guardadas.</p>
        </Card>
      ) : (
        <ul className="flex flex-col gap-4">
          {methods.map((m) => (
            <li key={m.id}>
              <PaymentMethodCard
                method={m}
                onSetDefault={() => setDefault(m.id)}
                onRemove={() => remove(m.id)}
                disableRemove={methods.length === 1}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PaymentMethodCard({
  method,
  onSetDefault,
  onRemove,
  disableRemove,
}: {
  method: MockPaymentMethod;
  onSetDefault: () => void;
  onRemove: () => void;
  disableRemove: boolean;
}): JSX.Element {
  const brandLabel = method.brand.charAt(0).toUpperCase() + method.brand.slice(1);
  const expLabel = `${String(method.expMonth).padStart(2, '0')}/${String(method.expYear).slice(-2)}`;
  return (
    <Card variant="default" padded>
      <div className="flex flex-col items-start gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <div
            aria-hidden
            className="flex h-10 w-16 flex-none items-center justify-center rounded-card bg-neutral-100 text-xs font-semibold uppercase tracking-wide text-neutral-700"
          >
            {brandLabel}
          </div>
          <div>
            <div className="font-medium text-fc-ink">•••• {method.last4}</div>
            <div className="text-xs text-fc-muted">
              {method.cardholderName} · Vence {expLabel}
            </div>
          </div>
          {method.isDefault && (
            <Badge tone="success" className="ml-2">
              Predeterminada
            </Badge>
          )}
        </div>
        <div className="flex gap-2">
          {!method.isDefault && (
            <Button variant="secondary" size="sm" onClick={onSetDefault}>
              Predeterminar
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemove}
            disabled={disableRemove}
            aria-label={`Quitar ${brandLabel} terminada en ${method.last4}`}
          >
            Quitar
          </Button>
        </div>
      </div>
    </Card>
  );
}
